import { useEffect, useState } from 'react'
import { supabase } from './lib/supabaseClient'
import { ThemeProvider, useTheme } from './context/ThemeContext'
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom'
import Login from './Login'
import LandingPage from './LandingPage'
import PricingPage from './components/PricingPage'
import OnboardingQuestionnaire from './components/OnboardingQuestionnaire'
import AboutUs from './AboutUs'
import Blog from './Blog'
import ResetPassword from './ResetPassword'
import DarkModeToggle from './components/DarkModeToggle'
import AuthCallback from './AuthCallback'
import InvitationAccept from './InvitationAccept'
import PrivacyPolicy from './PrivacyPolicy'
import TermsOfService from './TermsOfService'

// IMPORT YOUR ORIGINAL PORTAL COMPONENTS
import AgentPortal from './AgentPortal'
import LeadershipPortal from './LeadershipPortal'
import AccountingPortal from './AccountingPortal'
import MarketingPortal from './MarketingPortal'
import LegalPortal from './LegalPortal'
import RecruitmentPortal from './RecruitmentPortal'
import CROPortal from './CROPortal'

function AppContent() {
  const [session, setSession] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const [subscriptionActive, setSubscriptionActive] = useState(true)
  const [currentPlan, setCurrentPlan] = useState('free')
  const [companyId, setCompanyId] = useState(null)
  const { themeStyles } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('🔍 Session check on reload:', session ? '✅ Session exists' : '❌ No session')
      setSession(session)
      if (session) {
        checkUserStatus(session.user.id)
        fetchCurrentPlan(session.user?.company_id || companyId)
      }
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('🔄 Auth state changed:', _event, session ? '✅ Session exists' : '❌ No session')
      setSession(session)
      if (session) {
        checkUserStatus(session.user.id)
        fetchCurrentPlan(session.user?.company_id || companyId)
      } else {
        setUserRole(null)
        setCompanyId(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const checkUserStatus = async (userId) => {
    try {
      const { data: userData, error } = await supabase
        .from('users')
        .select('role, company_id')
        .eq('id', userId)
        .maybeSingle()
      
      if (error) {
        console.error('Error fetching user status:', error)
        return
      }
      
      if (userData) {
        setUserRole(userData.role || null)
        setCompanyId(userData.company_id || null)
        
        if (session && session.user) {
          session.user.company_id = userData.company_id
          session.user.role = userData.role
        }
      }
    } catch (err) {
      console.error('Failed to check user status:', err)
    }
  }

  const fetchCurrentPlan = async (companyIdParam) => {
    const companyIdToUse = companyIdParam || companyId
    
    if (!companyIdToUse) {
      return
    }
    
    try {
      const { data: company, error } = await supabase
        .from('companies')
        .select('subscription_plan')
        .eq('id', companyIdToUse)
        .maybeSingle()
      
      if (company) {
        setCurrentPlan(company.subscription_plan || 'free')
      }
    } catch (err) {
      console.error('Failed to fetch current plan:', err)
    }
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setSession(null)
    setUserRole(null)
    setCompanyId(null)
    navigate('/login')
  }

  const handleCompleteOnboarding = async (selectedRole) => {
    if (session) {
      try {
        const { error: userError } = await supabase
          .from('users')
          .upsert({ 
            id: session.user.id, 
            email: session.user.email, 
            role: selectedRole,
            onboarding_completed: true
          })
        
        if (userError) {
          console.error('Error updating user role:', userError)
          return
        }
        
        await checkUserStatus(session.user.id)
        setUserRole(selectedRole)
        navigate(`/${selectedRole}`)
      } catch (err) {
        console.error('Failed to complete onboarding:', err)
      }
    }
  }

  const handleSelectPlan = async (plan) => {
    setSubscriptionActive(true)
    
    if (session && companyId) {
      try {
        await supabase.from('company_settings').upsert({
          company_id: companyId,
          subscription_status: 'active',
          plan_id: plan.id,
          subscription_plan: plan.id,
          trial_ends_at: null
        })
        
        setCurrentPlan(plan.id)
        
        await supabase
          .from('companies')
          .update({ subscription_plan: plan.id })
          .eq('id', companyId)
        
        alert(`You've selected the ${plan.name} plan!`)
      } catch (err) {
        console.error('Failed to update plan:', err)
        alert('There was an error updating your plan. Please try again.')
      }
    } else {
      alert('Please log in again to update your subscription.')
    }
  }

  const DarkModeButton = () => (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 1000 }}>
      <DarkModeToggle />
    </div>
  )

  const portalProps = { 
    session, 
    onLogout: handleLogout,
    subscriptionActive 
  }

  if (loading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: 50, 
        background: themeStyles?.background || '#f8fafc', 
        color: themeStyles?.textColor || '#1e293b',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ fontSize: 48, marginBottom: 20 }}>🏢</div>
        <h2>Loading EstatesFlow CRM...</h2>
        <p>Please wait...</p>
      </div>
    )
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/invite/:invitationId" element={<InvitationAccept />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />

      {/* Onboarding Route */}
      <Route path="/onboarding" element={
        session && !userRole ? (
          <OnboardingQuestionnaire 
            user={session.user} 
            onComplete={handleCompleteOnboarding} 
          />
        ) : session && userRole ? (
          <Navigate to={`/${userRole}`} replace />
        ) : (
          <Navigate to="/login" replace />
        )
      } />

      {/* Pricing Route */}
      <Route path="/pricing" element={
        session ? (
          <PricingPage 
            onSelectPlan={handleSelectPlan}
            currentPlan={currentPlan}
            companyId={companyId}
            session={session}
          />
        ) : (
          <Navigate to="/login" replace />
        )
      } />
      
      {/* ALL PORTAL ROUTES - Using YOUR original components */}
      <Route path="/cro" element={
        session && userRole === 'cro' ? (
          <div>
            <DarkModeButton />
            <CROPortal {...portalProps} />
          </div>
        ) : <Navigate to={session ? '/' : '/login'} replace />
      } />
      
      <Route path="/leadership" element={
        session && userRole === 'leadership' ? (
          <div>
            <DarkModeButton />
            <LeadershipPortal {...portalProps} />
          </div>
        ) : <Navigate to={session ? '/' : '/login'} replace />
      } />
      
      <Route path="/accounting" element={
        session && userRole === 'accounting' ? (
          <div>
            <DarkModeButton />
            <AccountingPortal {...portalProps} />
          </div>
        ) : <Navigate to={session ? '/' : '/login'} replace />
      } />
      
      <Route path="/marketing" element={
        session && userRole === 'marketing' ? (
          <div>
            <DarkModeButton />
            <MarketingPortal {...portalProps} />
          </div>
        ) : <Navigate to={session ? '/' : '/login'} replace />
      } />
      
      <Route path="/legal" element={
        session && userRole === 'legal' ? (
          <div>
            <DarkModeButton />
            <LegalPortal {...portalProps} />
          </div>
        ) : <Navigate to={session ? '/' : '/login'} replace />
      } />
      
      <Route path="/recruitment" element={
        session && userRole === 'recruitment' ? (
          <div>
            <DarkModeButton />
            <RecruitmentPortal {...portalProps} />
          </div>
        ) : <Navigate to={session ? '/' : '/login'} replace />
      } />
      
      <Route path="/agent" element={
        session && userRole === 'agent' ? (
          <div>
            <DarkModeButton />
            <AgentPortal {...portalProps} />
          </div>
        ) : <Navigate to={session ? '/' : '/login'} replace />
      } />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  )
}

export default App